import React, { useState } from "react";
import { Mail, Trash2, AlertTriangle, CheckCircle } from "lucide-react";

export default function UserDeletion() {
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Send deletion request email
    const subject = encodeURIComponent("Account Deletion Request");
    const body = encodeURIComponent(
      `I would like to request deletion of my account.\n\n` +
      `Email: ${email}\n` +
      `Reason: ${reason}\n\n` +
      `Please process this request in accordance with GDPR/privacy regulations.`
    );
    
    window.location.href = `mailto:privacy@giopilos.com?subject=${subject}&body=${body}`;
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-6">
        <div className="max-w-md w-full backdrop-blur-xl bg-purple-900/20 border border-emerald-500/50 rounded-lg p-8 text-center">
          <CheckCircle size={64} className="mx-auto mb-6 text-emerald-400" />
          <h2 className="text-2xl font-bold text-white mb-4">Request Submitted</h2>
          <p className="text-zinc-300 mb-6">
            Your deletion request has been submitted. We will process your request within 30 days 
            as required by GDPR regulations.
          </p>
          <p className="text-sm text-zinc-400">
            You will receive a confirmation email at: <strong className="text-zinc-200">{email}</strong>
          </p>
          <a 
            href="/"
            className="mt-6 inline-block px-6 py-3 bg-emerald-600/90 hover:bg-emerald-700 rounded text-white font-medium transition backdrop-blur-xl"
          >
            Return Home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="container mx-auto px-6 py-12 max-w-2xl">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <Trash2 size={32} className="text-red-400" />
            <h1 className="text-4xl font-bold text-white">Account Deletion</h1>
          </div>
          <p className="text-zinc-400">Request permanent deletion of your account and data</p>
        </div>

        {/* Warning */}
        <div className="backdrop-blur-xl bg-red-900/20 border border-red-500/50 rounded-lg p-6 mb-8">
          <div className="flex items-start gap-3">
            <AlertTriangle size={24} className="text-red-400 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-lg font-semibold text-red-400 mb-2">Warning: This Action is Permanent</h3>
              <p className="text-zinc-300 text-sm leading-relaxed">
                Once your account is deleted, all your data including purchase history, subscriptions, 
                and saved content will be permanently removed. This action cannot be undone.
              </p>
            </div>
          </div>
        </div>

        {/* Information */}
        <div className="backdrop-blur-xl bg-purple-900/20 border border-white/10 rounded-lg p-6 mb-8">
          <h3 className="text-lg font-semibold text-white mb-4">What Will Be Deleted:</h3>
          <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4">
            <li>Your account and login credentials</li>
            <li>Personal information and profile data</li>
            <li>Purchase history and payment information</li>
            <li>Subscription status and access to content</li>
            <li>Any saved preferences or settings</li>
          </ul>
        </div>

        {/* Deletion Form */}
        <form onSubmit={handleSubmit} className="backdrop-blur-xl bg-purple-900/20 border border-white/10 rounded-lg p-8">
          <h3 className="text-xl font-semibold text-white mb-6">Deletion Request Form</h3>
          
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="your.email@example.com"
                className="w-full bg-black/40 border border-white/10 rounded px-4 py-3 text-white focus:outline-none focus:border-purple-500"
              />
              <p className="text-xs text-zinc-500 mt-1">
                Enter the email associated with your account
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                Reason for Deletion (Optional)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={4}
                placeholder="Help us improve by telling us why you're leaving..."
                className="w-full bg-black/40 border border-white/10 rounded px-4 py-3 text-white focus:outline-none focus:border-purple-500 resize-none"
              />
            </div>

            <div className="border-t border-white/10 pt-6">
              <button
                type="submit"
                className="w-full px-6 py-3 bg-red-600/90 hover:bg-red-700 rounded text-white font-medium flex items-center justify-center gap-2 transition backdrop-blur-xl"
              >
                <Trash2 size={18} />
                Submit Deletion Request
              </button>
            </div>
          </div>
        </form>

        {/* Contact */}
        <div className="mt-8 text-center">
          <p className="text-zinc-400 text-sm mb-2">Need help or have questions?</p>
          <a 
            href="mailto:privacy@giopilos.com"
            className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 transition"
          >
            <Mail size={16} />
            privacy@giopilos.com
          </a>
        </div>
      </div>
    </div>
  );
}
